import {Box, Grid, IconButton, Input, Select, Text, Tooltip} from "@chakra-ui/react";
import {MarkerSpot, RuleEventType, RuleType, SpotInfo, TriggerRule, TriggerRules} from "../../../types";
import {AppContext, AppContextI} from "../../../context/ContextWrapper";
import {ChangeEvent, KeyboardEvent, useContext, useEffect, useState} from "react";
import {BiTrash} from "react-icons/bi";
import {useConfirmDialog} from "../../../common/ConfirmDialogProvider";
import {isDanglingRule} from "../../../rules/ruleReferences";
import {compareVersions} from "../../../common/compareVersions";

type Props = {
    markerSpot: MarkerSpot,
    rule: TriggerRule,
    index: number,
    allSpotsInfo: SpotInfo[]
}

const SPOT_EVENT_OPTIONS: Array<{value: RuleEventType; label: string; requiresGeo?: boolean}> = [
    {value: "SPOT_TRIGGERED", label: "Spot has been triggered", requiresGeo: true},
    {value: "SPOT_NOT_TRIGGERED", label: "Spot has NOT been triggered", requiresGeo: true},
    {value: "SPOT_FINISHED", label: "Spot has fully played"},
    {value: "SPOT_FINISHED_FOR_MS", label: "Minimum time since spot fully played"},
    {value: "SPOT_NOT_FINISHED", label: "Spot has NOT fully played"}
];

const AMBIENT_EVENT_OPTIONS: Array<{value: RuleEventType; label: string; minVersion?: string}> = [
    {value: "AMBIENT_TRIGGERED", label: "Ambient zone has been triggered"},
    {value: "AMBIENT_NOT_TRIGGERED", label: "Ambient zone has NOT been triggered"},
    {value: "AMBIENT_TIME_SINCE_TRIGGERED", label: "Minimum time since ambient zone triggered", minVersion: "4.2"}
];

const RuleDetails = ({markerSpot, rule, allSpotsInfo}: Props) => {
    const appContext: AppContextI = useContext<AppContextI>(AppContext);
    const {confirm} = useConfirmDialog();
    const [minSinceFinishedInput, setMinSinceFinishedInput] = useState<string>("");
    const [minSinceTriggeredInput, setMinSinceTriggeredInput] = useState<string>("");
    const selection = appContext.getSelection();
    const isOnDemand = appContext.isOnDemand();
    const orgAppVersion = appContext.getOrganizationAppVersion() || "3.15";
    const isAmbientRuleTypeAvailable = compareVersions(orgAppVersion, "4.1") >= 0;
    const isAmbient = rule.type === "Ambient";
    const ambientZones = appContext.getArea()?.zones?.ambientZones || [];
    const allSpots = appContext.getSpots() || [];
    const isDangling = isDanglingRule(rule, allSpots, ambientZones);
    const danglingKey = isDangling ? (isAmbient ? rule.zoneKey : rule.spotKey) : undefined;

    const eventTypeOptions = isAmbient
        ? AMBIENT_EVENT_OPTIONS.filter((option) => !option.minVersion || compareVersions(orgAppVersion, option.minVersion) >= 0)
        : SPOT_EVENT_OPTIONS.filter((option) => !option.requiresGeo || !isOnDemand);
    const selectedEventTypeAvailable = eventTypeOptions.some((option) => option.value === rule.eventType);

    const updateRule = (updater: (triggerRule: TriggerRule) => void) => {
        const rules = markerSpot.triggerRules?.rules ? markerSpot.triggerRules.rules : [];
        rules.forEach((triggerRule: TriggerRule) => {
            if (triggerRule.id === rule.id) {
                updater(triggerRule);
            }
        });
        appContext.updateSpot(markerSpot, {
            ...markerSpot,
            triggerRules: {
                rules: rules,
                logic: markerSpot.triggerRules?.logic
            } as TriggerRules
        } as MarkerSpot);
    };

    const setRuleType = (evt: ChangeEvent<HTMLSelectElement>) => {
        const newType = evt.target.value as RuleType;
        updateRule((triggerRule) => {
            triggerRule.type = newType;
            if (newType === "Ambient") {
                triggerRule.eventType = "AMBIENT_TRIGGERED";
                triggerRule.spotKey = undefined;
            } else {
                triggerRule.eventType = isOnDemand ? "SPOT_FINISHED" : "SPOT_TRIGGERED";
                triggerRule.zoneKey = undefined;
                triggerRule.minMsSinceTriggered = undefined;
            }
        });
    };

    const setEventType = (evt: ChangeEvent<HTMLSelectElement>) => {
        const newEventType = evt.target.value;
        updateRule((triggerRule) => {
            triggerRule.eventType = newEventType as RuleEventType;
            if (newEventType === "SPOT_FINISHED_FOR_MS") {
                const currentValue = triggerRule.minMsSinceFinished;
                triggerRule.minMsSinceFinished = typeof currentValue === "number" && currentValue >= 1000
                    ? currentValue
                    : 1000;
            }
            if (newEventType === "AMBIENT_TIME_SINCE_TRIGGERED") {
                const currentValue = triggerRule.minMsSinceTriggered;
                triggerRule.minMsSinceTriggered = typeof currentValue === "number" && currentValue >= 1000
                    ? currentValue
                    : 1000;
            }
        });
    };

    const setSpotKey = (evt: ChangeEvent<HTMLSelectElement>) => {
        const newSpotKey = evt.target.value;
        updateRule((triggerRule) => {
            triggerRule.spotKey = newSpotKey;
        });
    };

    const setZoneKey = (evt: ChangeEvent<HTMLSelectElement>) => {
        const newZoneKey = evt.target.value;
        updateRule((triggerRule) => {
            triggerRule.zoneKey = newZoneKey;
        });
    };

    const setMinMsSinceFinished = (evt: ChangeEvent<HTMLInputElement>) => {
        setMinSinceFinishedInput(evt.target.value);
    };

    const updateMinMsSinceFinished = (value?: number) => {
        updateRule((triggerRule) => {
            triggerRule.minMsSinceFinished = value;
        });
    };

    const setMinMsSinceTriggered = (evt: ChangeEvent<HTMLInputElement>) => {
        setMinSinceTriggeredInput(evt.target.value);
    };

    const updateMinMsSinceTriggered = (value?: number) => {
        updateRule((triggerRule) => {
            triggerRule.minMsSinceTriggered = value;
        });
    };

    const minSecondsValue = typeof rule.minMsSinceFinished === "number"
        ? String(Math.min(9999, Math.max(1, Math.round(rule.minMsSinceFinished / 1000))))
        : "1";

    const minTriggeredSecondsValue = typeof rule.minMsSinceTriggered === "number"
        ? String(Math.min(9999, Math.max(1, Math.round(rule.minMsSinceTriggered / 1000))))
        : "1";

    useEffect(() => {
        if (rule.eventType !== "SPOT_FINISHED_FOR_MS") {
            return;
        }
        setMinSinceFinishedInput(minSecondsValue);
    }, [minSecondsValue, rule.eventType, rule.id]);

    useEffect(() => {
        if (rule.eventType !== "AMBIENT_TIME_SINCE_TRIGGERED") {
            return;
        }
        setMinSinceTriggeredInput(minTriggeredSecondsValue);
    }, [minTriggeredSecondsValue, rule.eventType, rule.id]);

    const handleMinMsSinceFinishedBlur = () => {
        const parsed = Number(minSinceFinishedInput);
        const clampedSeconds = Number.isFinite(parsed)
            ? Math.min(9999, Math.max(1, Math.round(parsed)))
            : 1;
        setMinSinceFinishedInput(String(clampedSeconds));
        updateMinMsSinceFinished(clampedSeconds * 1000);
    };

    const handleMinMsSinceTriggeredBlur = () => {
        const parsed = Number(minSinceTriggeredInput);
        const clampedSeconds = Number.isFinite(parsed)
            ? Math.min(9999, Math.max(1, Math.round(parsed)))
            : 1;
        setMinSinceTriggeredInput(String(clampedSeconds));
        updateMinMsSinceTriggered(clampedSeconds * 1000);
    };

    return <>
        <Grid gridTemplateColumns={"max-content 1fr"}>

            <Box ml={"2"}>
                {
                    appContext.isSelected(markerSpot)
                        ?
                        <Grid gridTemplateColumns={"min-content 1fr"} columnGap={"2"}>
                            <Box>Type:</Box>
                            <Select
                                value={rule.type}
                                onChange={setRuleType}
                                size={"sm"}
                                disabled={!appContext.isDraft()}
                            >
                                <option value="Spot">Spot</option>
                                {isAmbientRuleTypeAvailable || isAmbient ? (
                                    <option value="Ambient">Ambient zone</option>
                                ) : null}
                            </Select>

                            <Box>Condition:</Box>
                            <Select
                                value={rule.eventType}
                                onChange={setEventType}
                                size={"sm"}
                                disabled={!appContext.isDraft()}
                            >
                                {!selectedEventTypeAvailable ? (
                                    <option value={rule.eventType}>
                                        {rule.eventType}
                                    </option>
                                ) : null}
                                {eventTypeOptions.map((option) => (
                                    <option key={option.value} value={option.value}>{option.label}</option>
                                ))}
                            </Select>

                            {isAmbient ? (
                                <>
                                    <Box whiteSpace={"nowrap"}>Ambient zone:</Box>
                                    <Tooltip
                                        label={isDangling ? `References missing ambient zone (key: ${danglingKey})` : ""}
                                        isDisabled={!isDangling}
                                    >
                                        <Select
                                            value={rule.zoneKey || "-1"}
                                            onChange={setZoneKey}
                                            size={"sm"}
                                            disabled={!appContext.isDraft()}
                                            borderColor={isDangling ? "red.400" : undefined}
                                            color={isDangling ? "red.500" : undefined}
                                        >
                                            <option value={"-1"}>Select a zone</option>
                                            {isDangling && rule.zoneKey ? (
                                                <option value={rule.zoneKey}>
                                                    {`(missing) ${rule.zoneKey}`}
                                                </option>
                                            ) : null}
                                            {ambientZones.map((zone) => (
                                                <option key={zone.id} value={zone.id}>
                                                    {zone.label || zone.name || zone.id}
                                                </option>
                                            ))}
                                        </Select>
                                    </Tooltip>
                                </>
                            ) : (
                                <>
                                    <Box>Spot:</Box>
                                    <Tooltip
                                        label={isDangling ? `References missing spot (key: ${danglingKey})` : ""}
                                        isDisabled={!isDangling}
                                    >
                                        <Select
                                            value={rule.spotKey || "-1"}
                                            onChange={setSpotKey}
                                            size={"sm"}
                                            disabled={!appContext.isDraft()}
                                            borderColor={isDangling ? "red.400" : undefined}
                                            color={isDangling ? "red.500" : undefined}
                                        >
                                            <option value={"-1"}>Select a spot</option>
                                            {isDangling && rule.spotKey ? (
                                                <option value={rule.spotKey}>
                                                    {`(missing) ${rule.spotKey}`}
                                                </option>
                                            ) : null}
                                            {
                                                allSpotsInfo
                                                    .filter(sp => sp.id !== markerSpot.id)
                                                    .map((spotInfo: SpotInfo) => {
                                                        return <option key={spotInfo.key}
                                                                       value={spotInfo.key}>{spotInfo.name}</option>
                                                    })
                                            }
                                        </Select>
                                    </Tooltip>
                                </>
                            )}
                            {isDangling ? (
                                <>
                                    <Box/>
                                    <Text color={"red.500"} fontSize={"sm"}>
                                        {isAmbient
                                            ? "This rule references an ambient zone that no longer exists. Update or delete the rule."
                                            : "This rule references a spot that no longer exists. Update or delete the rule."}
                                    </Text>
                                </>
                            ) : null}

                            {rule.eventType === "SPOT_FINISHED_FOR_MS" ? (
                                <>
                                    <Box>Time (s):</Box>
                                    <Input
                                        type={"number"}
                                        min={1}
                                        max={9999}
                                        step={1}
                                        width={"90px"}
                                        value={minSinceFinishedInput}
                                        onChange={setMinMsSinceFinished}
                                        onBlur={handleMinMsSinceFinishedBlur}
                                        onKeyDown={(event: KeyboardEvent<HTMLInputElement>) => {
                                            if (event.key === "Enter") {
                                                event.currentTarget.blur();
                                            }
                                        }}
                                        size={"sm"}
                                        disabled={!appContext.isDraft()}
                                    />
                                </>
                            ) : null}

                            {rule.eventType === "AMBIENT_TIME_SINCE_TRIGGERED" ? (
                                <>
                                    <Box>Time (s):</Box>
                                    <Input
                                        type={"number"}
                                        min={1}
                                        max={9999}
                                        step={1}
                                        width={"90px"}
                                        value={minSinceTriggeredInput}
                                        onChange={setMinMsSinceTriggered}
                                        onBlur={handleMinMsSinceTriggeredBlur}
                                        onKeyDown={(event: KeyboardEvent<HTMLInputElement>) => {
                                            if (event.key === "Enter") {
                                                event.currentTarget.blur();
                                            }
                                        }}
                                        size={"sm"}
                                        disabled={!appContext.isDraft()}
                                    />
                                </>
                            ) : null}
                        </Grid>
                        :

                        <Box>
                            <strong>{isAmbient ? rule.zoneKey : rule.spotKey}</strong>
                        </Box>
                }

            </Box>
        </Grid>

        <Box/>
        <IconButton variant={"ghost"} aria-label={"Delete"} icon={<BiTrash/>} onClick={async () => {
            if (!selection || !markerSpot) {
                return;
            }
            const approved = await confirm({
                title: "Delete rule",
                body: "Are you sure you want to delete this rule?",
                confirmText: "Delete",
                isDestructive: true
            });
            if (!approved) {
                return;
            }
            const newRules = [...markerSpot.triggerRules?.rules ? markerSpot.triggerRules.rules : []]
            const ruleTriggers = newRules.filter((triggerRule: TriggerRule) => triggerRule.id !== rule.id);

            appContext.updateSpot(selection, {
                ...selection,
                triggerRules: {
                    rules: ruleTriggers,
                    logic: markerSpot.triggerRules?.logic
                }
            } as MarkerSpot);
        }}
                    disabled={!appContext.isDraft()}
        />
    </>;
}

export default RuleDetails
